// 负责广告脚本加载和初始化
// 处理广告元素的可视区域检测(Intersection Observer)
// 管理广告加载重试机制
import { emitAdTelemetryV2, initAdTelemetryV2, scheduleAdTelemetryV2 } from '@/utils/adTelemetryV2'

export default {
	data() {
		return {
			adObserver: null,
			domObserver: null,
			observedAds: new Set(),
			adLoadRetries: new Map(),
			maxRetries: 3,
			preloadDistance: 300, // 改为300px的预加载距离
		}
	},

	async mounted() {
		try {
			await this.initAdSystem()
			// If the component also uses adTrackingMixin, initAdTracking will be available.
			if (typeof this.initAdTracking === 'function') {
				this.$nextTick(() => {
					this.initAdTracking()
				})
			}
		} catch (error) {
			console.error(`[AdMixin] Ad system initialization error in ${this.$options.name}:`, error)
		}
	},
	beforeDestroy() {
		this.cleanupAdSystem()
	},
	methods: {
		async initAdSystem() {
			console.log(`[AdMixin] 🚀 初始化广告系统 | 组件: ${this.$options.name}`)
			if (typeof window !== 'undefined') {
				await this.loadAdSenseScript()
				initAdTelemetryV2(this.adsensConfig)
				this.$nextTick(() => {
					setTimeout(() => {
						this.setupAdObserver()
					}, 100)
				})
			}
		},

		async loadAdSenseScript() {
			if (window.adsbygoogle?.loaded) {
				this.$eventrack('adscript_loaded', 'expose')
				scheduleAdTelemetryV2('status_change', 'adscript_already_loaded')
				return
			}

			if (!this.adsensConfig?.scriptUrl) {
				this.$eventrack('no_adscript_config', 'expose')
				scheduleAdTelemetryV2('status_change', 'no_adscript_config')
				return
			}

			try {
				await this.loadScript(this.adsensConfig.scriptUrl)
				this.$eventrack('adscript_add_success', 'expose')
				scheduleAdTelemetryV2('status_change', 'adscript_add_success')
			} catch (error) {
				console.error('Failed to load AdSense script:', error)
				this.$eventrack('adscript_load_error', 'error')
				scheduleAdTelemetryV2('status_change', 'adscript_load_error')
			}
		},

		loadScript(url) {
			return new Promise((resolve, reject) => {
				const existing = Array.from(document.scripts).find((script) => script.src === url)
				if (existing) {
					if (window.adsbygoogle?.loaded) {
						resolve()
						return
					}
					existing.addEventListener('load', resolve, { once: true })
					existing.addEventListener('error', reject, { once: true })
					return
				}

				const script = document.createElement('script')
				script.src = url
				script.async = true
				script.crossOrigin = 'anonymous'
				script.onload = resolve
				script.onerror = reject
				document.head.appendChild(script)
			})
		},

		setupAdObserver() {
			if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
				return
			}

			if (this.adObserver) {
				this.adObserver.disconnect()
			}

			this.observedAds = new Set()

			console.log(`[AdMixin] 👀 启动监听 | 预加载: ${this.preloadDistance}px`)

			this.adObserver = new IntersectionObserver(
				(entries) => {
					// 移除冗余日志
					// console.log('观察对象⬇️')
					// console.log(entries)
					entries.forEach(this.handleAdIntersection)
				},
				{
					// rootMargin 格式: top right bottom left
					// 同时扩展上下，支持双向滚动的预加载
					rootMargin: `${this.preloadDistance}px 0px ${this.preloadDistance}px 0px`,
					threshold: 0,
				},
			)

			const debounce = (func, delay) => {
				let timeout
				return (...args) => {
					clearTimeout(timeout)
					timeout = setTimeout(() => {
						func.apply(this, args)
					}, delay)
				}
			}
			const debouncedObserveAds = debounce(this.observeAds, 300)

			this.$nextTick(() => {
				setTimeout(() => {
					this.observeAds()
				}, 200)

				if (typeof MutationObserver !== 'undefined' && !this.domObserver) {
					this.domObserver = new MutationObserver(() => {
						// 降低 DOM 变化日志级别或移除
						// console.log(`[AdMixin] DOM changed in ${this.$options.name}, re-observing ads.`)
						debouncedObserveAds()
					})

					this.domObserver.observe(this.$el, {
						childList: true,
						subtree: true,
					})
				}
			})
		},

		observeAds() {
			// 改为通过 DOM 查询来查找所有广告元素，而不是依赖 this.$refs
			// 这样可以找到所有子组件中的广告
			const adElements = this.$el.querySelectorAll('ins.adsbygoogle');
			const newAdsCount = Array.from(adElements).filter(el => !this.observedAds.has(el)).length;

			if (newAdsCount > 0) {
				console.log(`[AdMixin] 🔍 发现 ${newAdsCount} 个新广告元素 (总数: ${adElements.length})`);
				scheduleAdTelemetryV2('status_change', 'observe_ads')
			}

			if (adElements.length > 0) {
				adElements.forEach((adElement) => {
					// 检查它是否已经被观察或已经有状态
					if (!adElement.getAttribute('data-ad-status') && !this.observedAds.has(adElement)) {
						const slot = adElement.getAttribute('data-ad-slot')
						console.log(`[AdMixin] ➕ 添加监听 | Slot: ${slot}`);

						this.observedAds.add(adElement);
						this.adObserver.observe(adElement);
					}
				});
			}
		},

		handleAdIntersection(entry) {
			if (!entry || !this.observedAds) return

			const adElement = entry.target
			const adid = adElement.getAttribute('data-ad-slot')
			if (entry.isIntersecting && adElement && !adElement.getAttribute('data-ad-status')) {
				console.log(`[AdMixin] ✅ 命中预加载 | Slot: ${adid}`)

				// 移除详细坐标信息，保持简洁
				/*
				const rect = adElement.getBoundingClientRect()
				console.log(`[AdMixin] ✅ 广告进入预加载区域: ${adid}`, {
					position: {
						pageTop: Math.round(rect.top + window.pageYOffset),
						viewportTop: Math.round(rect.top),
					},
					intersectionRatio: entry.intersectionRatio,
				})
				*/

				this.adObserver.unobserve(adElement)
				this.observedAds.delete(adElement)
				scheduleAdTelemetryV2('scroll', 'ad_intersection')
				this.loadAd(adElement)
			}
		},

		async loadAd(adElement, retryCount = 0) {
			if (adElement.getAttribute('data-ad-status')) {
				return
			}

			if (!window.adsbygoogle) {
				if (retryCount < this.maxRetries) {
					console.log(`[AdMixin] 🔄 重试广告加载 (${retryCount + 1}/${this.maxRetries})...`)
					setTimeout(() => {
						this.loadAd(adElement, retryCount + 1)
					}, 1000)
					return
				}
				console.warn('[AdMixin] ❌ AdSense 脚本未加载，广告加载失败')
				adElement.setAttribute('data-ad-status', 'error')
				emitAdTelemetryV2('status_change', 'adsbygoogle_missing')
				return
			}

			try {
				if (adElement && document.body.contains(adElement)) {
					await this.$nextTick()
					if (adElement.offsetWidth > 0) {
						const adSlot = adElement.getAttribute('data-ad-slot')
						console.log(`[AdMixin] 📣 Push | Slot: ${adSlot} | Size: ${adElement.offsetWidth}x${adElement.offsetHeight}`)
						;(window.adsbygoogle = window.adsbygoogle || []).push({})
						adElement.setAttribute('data-ad-status', 'loaded')
						emitAdTelemetryV2('status_change', 'adsbygoogle_push')
					} else {
						console.log(`[AdMixin] ⏳ 等待渲染 | Slot: ${adElement.getAttribute('data-ad-slot')}`)
						if (retryCount < this.maxRetries) {
							setTimeout(() => {
								this.loadAd(adElement, retryCount + 1)
							}, 500)
						}
					}
				}
			} catch (error) {
				console.error(`[AdMixin] ❌ 广告推送错误:`, error)
				if (retryCount < this.maxRetries) {
					console.log(`[AdMixin] 🔄 错误后重试 (${retryCount + 1}/${this.maxRetries})`)
					setTimeout(() => {
						this.loadAd(adElement, retryCount + 1)
					}, 1000)
				} else {
					adElement.setAttribute('data-ad-status', 'error')
					emitAdTelemetryV2('status_change', 'adsbygoogle_push_error')
				}
			}
		},

		cleanupAdSystem() {
			if (this.adObserver) {
				this.adObserver.disconnect()
				this.adObserver = null
			}
			if (this.domObserver) {
				this.domObserver.disconnect()
				this.domObserver = null
			}
			this.observedAds.clear()
		},
	},
}
